import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const Policies = () => {
  const [activePolicy, setActivePolicy] = useState('general');

  const policyCategories = [
    { id: 'general', name: 'General Policies', icon: 'fa-file-lines' },
    { id: 'facility', name: 'Facility Usage', icon: 'fa-building' },
    { id: 'membership', name: 'Membership Rules', icon: 'fa-users' },
    { id: 'conduct', name: 'Code of Conduct', icon: 'fa-handshake' },
    { id: 'payment', name: 'Payment & Fees', icon: 'fa-credit-card' },
    { id: 'safety', name: 'Safety & Security', icon: 'fa-shield' }
  ];

  const policies = {
    general: [
      { title: 'Club Hours of Operation', content: 'The club is open from 6:00 AM to 11:00 PM daily, including public holidays. Some facilities may have varying hours.' },
      { title: 'Guest Policy', content: 'Members may bring up to 3 guests per visit. Guests must be accompanied by the member at all times and comply with all club rules.' },
      { title: 'Dress Code', content: 'Appropriate attire is required in all areas of the club. Sports facilities require proper athletic wear. Fine dining areas require smart casual or formal attire.' },
      { title: 'Photography & Recording', content: 'Photography and recording are permitted for personal use only. Commercial photography requires prior approval from management.' }
    ],
    facility: [
      { title: 'Booking Procedures', content: 'Facilities can be booked up to 7 days in advance through the member portal, mobile app, or front desk.' },
      { title: 'Cancellation Policy', content: 'Cancellations must be made at least 24 hours in advance to avoid charges. Late cancellations may incur a fee.' },
      { title: 'Facility Etiquette', content: 'Please respect other members by limiting usage to designated time slots and cleaning up after use.' },
      { title: 'Equipment Usage', content: 'Equipment must be used as intended. Report any damaged equipment to staff immediately.' }
    ],
    membership: [
      { title: 'Membership Renewal', content: 'Memberships are renewed annually. Renewal notices are sent 30 days before expiration.' },
      { title: 'Membership Suspension', content: 'Members may request temporary suspension for up to 3 months per year due to travel or medical reasons.' },
      { title: 'Transfer of Membership', content: 'Memberships are non-transferable except for family members as defined in the membership agreement.' },
      { title: 'Termination', content: 'The club reserves the right to terminate membership for violation of policies or non-payment of dues.' }
    ],
    conduct: [
      { title: 'Respectful Behavior', content: 'Treat all members, staff, and guests with courtesy and respect. Harassment or discrimination will not be tolerated.' },
      { title: 'Alcohol Policy', content: 'Alcohol consumption is permitted only in designated areas. Members are responsible for their guests\' behavior.' },
      { title: 'Smoking Policy', content: 'Smoking is prohibited in all indoor areas. Designated outdoor smoking areas are available.' },
      { title: 'Dispute Resolution', content: 'Disputes between members should be reported to management for resolution.' }
    ],
    payment: [
      { title: 'Membership Dues', content: 'Dues are payable monthly or annually. Late payments may incur a 5% penalty fee.' },
      { title: 'Additional Charges', content: 'Guest fees, event tickets, and special services are billed to the member account and due within 30 days.' },
      { title: 'Refund Policy', content: 'Membership fees are non-refundable except in cases of medical emergency or relocation.' },
      { title: 'Payment Methods', content: 'We accept bank transfers, credit cards, and direct debit. Online payments can be made through the member portal.' }
    ],
    safety: [
      { title: 'Emergency Procedures', content: 'In case of emergency, contact club staff immediately. Emergency exits are clearly marked throughout the facility.' },
      { title: 'Health & Safety', content: 'Members must follow all posted safety guidelines. Report any hazards to staff.' },
      { title: 'Child Supervision', content: 'Children under 12 must be supervised by an adult at all times in all areas of the club.' },
      { title: 'Lost & Found', content: 'Lost items should be reported to the front desk. Items are kept for 30 days.' }
    ]
  };

  return (
    <div className="policies-page">
      {/* Hero Section */}
      <section className="page-hero policies-hero">
        <div className="hero-overlay"></div>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="hero-eyebrow">THE CLUB</span>
            <h1 className="hero-title">Rules & <span className="text-green">Policies</span></h1>
            <p className="hero-subtitle">Guidelines to ensure a safe, enjoyable experience for all members</p>
          </motion.div>
        </div>
      </section>

      {/* Policies Navigation */}
      <section className="policies-nav-section">
        <div className="container">
          <div className="policies-nav">
            {policyCategories.map(cat => (
              <button 
                key={cat.id}
                className={`policy-nav-btn ${activePolicy === cat.id ? 'active' : ''}`}
                onClick={() => setActivePolicy(cat.id)}
              >
                <i className={`fas ${cat.icon}`}></i>
                <span>{cat.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Policies Content */}
      <section className="policies-content-section">
        <div className="container">
          <AnimatePresence mode="wait">
            <motion.div 
              key={activePolicy}
              className="policies-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h2>{policyCategories.find(c => c.id === activePolicy)?.name}</h2>
              <div className="policies-list">
                {policies[activePolicy].map((policy, index) => (
                  <motion.div 
                    key={index}
                    className="policy-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <h3>{policy.title}</h3>
                    <p>{policy.content}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Download Section */}
      <section className="policies-download-section">
        <div className="container">
          <div className="download-card">
            <div className="download-content">
              <h3>Club Constitution</h3>
              <p>Download the official NLNG RA Club Constitution with 2026 AGM updates in PDF format.</p>
            </div>
            <div className="download-buttons">
              <a href="/NLNG RA Club Constitution - 2026 AGM Updates.pdf" className="btn btn-primary" download>
                <i className="fas fa-download"></i> Download PDF
              </a>
              <Link to="/contact" className="btn btn-outline">
                <i className="fas fa-envelope"></i> Questions?
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Policies;